import { useCallback, useMemo } from 'react';
import { useSnackbar, SnackbarKey } from 'notistack';

import B2ChatToastContent from './B2ChatToastContent';
import { TOAST_ANCHOR_ORIGIN, TOAST_DURATION } from './constants';
import { B2ChatToastApi, ToastOptions, ToastVariant } from './types';

export default function useB2ChatToast(): B2ChatToastApi {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const show = useCallback(
    (
      variant: ToastVariant,
      message: string,
      options?: ToastOptions
    ): SnackbarKey => {
      const duration = options?.persist
        ? null
        : options?.duration ?? TOAST_DURATION[variant];

      return enqueueSnackbar(message, {
        autoHideDuration: duration,
        persist: options?.persist ?? false,
        preventDuplicate: options?.preventDuplicate ?? true,
        anchorOrigin: TOAST_ANCHOR_ORIGIN,
        content: (_key, msg) => (
          <B2ChatToastContent
            variant={variant}
            message={msg as string}
            title={options?.title}
            onClose={() => closeSnackbar(_key)}
            action={options?.action}
          />
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  return useMemo(
    () => ({
      success: (msg: string, opts?: ToastOptions) =>
        show(ToastVariant.SUCCESS, msg, opts),
      error: (msg: string, opts?: ToastOptions) =>
        show(ToastVariant.ERROR, msg, opts),
      warning: (msg: string, opts?: ToastOptions) =>
        show(ToastVariant.WARNING, msg, opts),
      info: (msg: string, opts?: ToastOptions) =>
        show(ToastVariant.INFO, msg, opts),
      close: closeSnackbar,
    }),
    [show, closeSnackbar]
  );
}
